import type { GatsbyBrowser } from "gatsby";
import { bootstrapGA, sendGAEvent, sendSectionView } from "@utils/ga";
import { bootstrapMixpanel, sendAnonymousVisit, trackPageView, trackSectionView } from "@utils/mixpanel";

function attachSectionObservers(sectionIds: string[]) {
  if (typeof window === "undefined") return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          sendGAEvent("section_view", {
            section_name: id,
            trigger_type: "scroll",
            page_location: window.location.href,
          });
          // optional: disconnect after first hit so it fires only once
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.3 } // triggers when 30% of section visible
  );

  sectionIds.forEach((id) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
    else console.warn(`[GA4] Section #${id} not found`);
  });
}

export const onClientEntry = () => {
  console.log("[GA4] onClientEntry → bootstrapGA()");
  bootstrapGA();;
  bootstrapMixpanel()
  sendAnonymousVisit();  // one anonymous ping per page load
};

// ---- Gatsby API ---- //
export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location, prevLocation }) => {
  if (typeof window === "undefined") return;

  const newPath = location.pathname + location.search;
  const oldPath = prevLocation
    ? prevLocation.pathname + prevLocation.search
    : undefined;

  const newHash = location.hash;
  const oldHash = prevLocation?.hash;

  // True route change (path or search different) or only HASH change?
  const isTrueNavigation = !oldPath || newPath !== oldPath;
  const isHashOnlyChange = !isTrueNavigation && newHash !== oldHash;

  const page_location = window.location.href;

  if (isTrueNavigation) {
    // Send manual GA4 page_view for real navigations
    sendGAEvent("page_view", {
      page_path: location.pathname + location.search + location.hash,
      page_location,
    });
    console.log("[GA4] page_view:", location.pathname + location.search + location.hash);

    // --- Mixpanel: Manual page_view
    trackPageView(location.pathname + location.search + location.hash);
    console.log("[Mixpanel] page_view:", location.pathname + location.search + location.hash);

    // If this route load includes a hash (direct deep link)
    if (newHash) {
      sendSectionView(newHash.replace("#", ""), "direct");
      trackSectionView(newHash.replace("#", ""), "direct");
    }
  } else if (isHashOnlyChange && newHash) {
    // Skip page_view; only section_view for in-page anchor clicks
    sendSectionView(newHash.replace("#", ""), "link");
    trackSectionView(newHash.replace("#", ""), "link");
  }

  // Attach scroll observer once per page load
  if (!(window as any).__shopObserverAttached__) {
    (window as any).__shopObserverAttached__ = true;
    attachSectionObservers(["features", "process", "getStarted", "faq", "technology"]);
  }
};
